import { push } from "connected-react-router";
import { useState, useMemo } from "react";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { fullName, humanReadableDate } from "../../../app/utils";
import {
  APPLICATION_MODE_NEW,
  APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE,
  APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE,
  DOCUMENT_CONTENT_TYPE_PDF,
  DOCUMENT_TYPE_CRIMINAL_RECORD,
  ROUTE_PATH,
  VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT,
  COMPANY_TYPES,
  MORTGAGE_PROPERTY_KEYS,
  DOCUMENT_TYPE_INSURANCE_CONTINURATION
} from "../../../app/constants";
import {
  Header,
  Button,
  Paragraph,
  LineSeperator,
  MainSeparator
} from "../../../components/RegularComponents";
import { CompanyContactInformation } from "../CompanyContactInformation";
import ReviewForm from "../../../components/ReviewForm/ReviewForm";
import TopNavigationBar from "../../TopNavigationBar";
import { setMortgageProperty } from "../../../redux/slice/primaryAgencyApplicationSlice";
import { saveBusinessLicenceApplicationData } from "../../../redux/slice/businessLicenceApplicationSlice";
import {
  DocumentViewerFileNameLink,
  DownloadFileNameLink
} from "../../../components/DocumentViewer/DocumentFileComponent";
import { convertFileDataForUrl } from "../../../app/documentUploadUtils";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import { fetchDocumentData } from "../../../redux/slice/supportingDocumentsSlice";
import { RenewalRegulationAct } from "./RenewalRegulationAct";
import { ConfirmAIRSubmission } from "../../MRA/Renewals/ConfirmAIRSubmission";
import { RenewalCompanyType } from "./RenewalCompanyType";
import { RenewalCompanyFinancialLocation } from "./RenewalCompanyFinancialLocation";
import { RenewalAppointedAgentServiceAddress } from "./RenewalAppointedAgentServiceAddress";
import { RenewalCompanyFiscalEndDate } from "./RenewalCompanyFiscalEndDate";
import { RenewalCriminalRecordCheck } from "./RenewalCriminalRecordCheck";
import { RenewalProofOfInsurance } from "./RenewalProofOfInsurance";
import { RenewalTrustAccount } from "./RenewalTrustAccount";
import { RenewalComplianceOfficerInformation } from "./RenewalComplianceOfficerInformation";
import { RenewalComplianceOfficerQualification } from "./RenewalComplianceOfficerQualification";
import {
  agencyApplicationSelector,
  cachePropertiesSelector
} from "../../../redux/selector/agencyApplicationSelector";
import { applicationTypeFromLicenceSubType } from "../../../redux/slice/applicationsUtils";

const MORTGAGE_APPLICATION_PROPERTY = "userApplicationReviewComplete";

export const RenewalApplicationReview = (props) => {
  const dispatch = useDispatch();
  const { agencyApplication } = useSelector(agencyApplicationSelector);

  const mortgageCacheProperties = useSelector(cachePropertiesSelector);

  const [openDocumentViewer, setOpenDocumentViewer] = useState(false);
  const [documentViewerDocument, setDocumentViewerDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const applicationType = applicationTypeFromLicenceSubType(agencyApplication.licencingSubType);
  const applicationMode = APPLICATION_MODE_NEW;
  const isLender = applicationType === APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE;
  const lastIssuedDate = agencyApplication?.issuedDate;

  const { mlApplicationState, documents } = useSelector((state) => ({
    mlApplicationState: state.primaryAgencyApplication.application?.businessLicenceData ?? {},
    documents: state.primaryAgencyApplication.application?.documents ?? []
  }));

  const uploadCriminalRecordCheck = documents.filter(
    (document) =>
      document.documentType === DOCUMENT_TYPE_CRIMINAL_RECORD && document.changeType !== "DELETE"
  );
  const uploadedProofOfInsurance = documents.filter(
    (document) =>
      document.documentType === DOCUMENT_TYPE_INSURANCE_CONTINURATION &&
      document.changeType !== "DELETE" &&
      (document.createdDate ? document.createdDate > lastIssuedDate : true)
  );

  // const uploadedAIRDocument = documents.filter(
  //   (document) =>
  //     document.documentType === DOCUMENT_TYPE_ANNUAL_INFORMATION_RETURN &&
  //     document.changeType !== "DELETE"
  // );

  const {
    financialRecordsLocation,
    responsibleAgent,
    serviceBusinessAddress,
    companyFiscalEndDate,
    companyRjscDetails,
    trustAccountInstitution,
    mraRenewalCrcNoChangeIds,
    mraRenewalCrcUpdatedIds
  } = mlApplicationState;

  const { businessData } = companyRjscDetails;

  const peopleList = useMemo(() => {
    if (businessData?.peopleList) {
      return businessData?.peopleList;
    }

    return [];
  }, [businessData]);

  const [editComponent, setEditComponent] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const COMPONENT_KEYS = {
    ML_COMPLIANCE: "mlCompliance",
    AIR_REPORT: "airReport",
    COMPANY_TYPE: "companyType",
    FINANCIAL_LOCATION: "financialLocaiton",
    APPOINTED_AGENT: "appointedAgent",
    FISCAL_END_DATE: "ficalEndDate",
    CRIMINAL_RECORD: "criminalRecord",
    PROOF_INSURANCE_OMISSIONS: "proofInsuranceOmissions",
    TRUST_ACCOUNT: "trustAccount",
    CO_PERSONAL_INFO: "COPersonalInfo",
    COMPLIANCE_OFFICER: "complianceOfficer"
  };

  const formatDate = (date) => {
    const months = {
      0: "January",
      1: "February",
      2: "March",
      3: "April",
      4: "May",
      5: "June",
      6: "July",
      7: "August",
      8: "September",
      9: "October",
      10: "November",
      11: "December"
    };

    const dateObj = new Date(date);

    return `${months[dateObj.getMonth()]} ${dateObj.getDate()}`;
  };

  const SubSeparator = () => {
    return (
      <hr
        className="bottomMarginNormal"
        style={{
          borderColor: "#0058a466",
          borderWidth: "2px",
          width: "70%",
          marginLeft: "0px"
        }}
      />
    );
  };

  const titleForComponent = (componentKey) => {
    switch (componentKey) {
      case COMPONENT_KEYS.ML_COMPLIANCE:
        if (applicationType === APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE) {
          return "Mortgage Administrator Compliance";
        }
        return "Mortgage Lender Compliance";
      case COMPONENT_KEYS.AIR_REPORT:
        return "Annual Information Return (AIR)";
      case COMPONENT_KEYS.COMPANY_TYPE:
        return "Company type";
      case COMPONENT_KEYS.PRINCIPLE_BROKER:
        return "Principal Broker";
      case COMPONENT_KEYS.FINANCIAL_LOCATION:
        return "Financial Records Location";
      case COMPONENT_KEYS.APPOINTED_AGENT:
        return "Appointed Agent";
      case COMPONENT_KEYS.FISCAL_END_DATE:
        return "Fiscal End Date";
      case COMPONENT_KEYS.CRIMINAL_RECORD:
        return "Criminal Record";
      case COMPONENT_KEYS.PROOF_INSURANCE_OMISSIONS:
        return "Proof of Errors and Omissions Insurance";
      case COMPONENT_KEYS.TRUST_ACCOUNT:
        return "Trust Account";
      case COMPONENT_KEYS.CO_PERSONAL_INFO:
        return "Compliance Officer personal information";
      case COMPONENT_KEYS.COMPLIANCE_OFFICER:
        return "Compliance Officer Qualifications"; //

      default:
        return "";
    }
  };

  const documentReviewComponent = (file) => {
    const fileComponents = [];

    if (file.documentContentType === DOCUMENT_CONTENT_TYPE_PDF) {
      fileComponents.push(
        <div style={{ maxWidth: "40rem", overflow: "hidden" }}>
          <DocumentViewerFileNameLink
            id={"document-view-link-" + file.fileName}
            key={"document-view-link-" + file.fileName}
            file={file}
            onClick={() => {
              if (file.data) {
                let fileDataUrl = convertFileDataForUrl(file.data, file.documentContentType);
                setDocumentViewerDocument(fileDataUrl);
                setSelectedFile(file);
                setOpenDocumentViewer(true);
              } else {
                // download file data and then show it
                dispatch(fetchDocumentData(file.guid))
                  .then((downloadFile) => {
                    if (downloadFile) {
                      let fileDataUrl = convertFileDataForUrl(
                        downloadFile.data,
                        downloadFile.documentContentType
                      );
                      setDocumentViewerDocument(fileDataUrl);
                      setSelectedFile(downloadFile);
                      setOpenDocumentViewer(true);
                    }
                  })
                  .catch((error) => {
                    console.error(error);
                  });
              }
            }}
          />
        </div>
      );
    } else {
      fileComponents.push(
        <DownloadFileNameLink
          id={"download-link-" + file.fileName}
          componentKey={"download-link-" + file.fileName}
          file={file}
        />
      );
    }

    return fileComponents;
  };

  /*=== click handlers == */

  const handleEditCOPersonalInformation = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.CO_PERSONAL_INFO);
  };

  const handleEditCOQualifications = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.COMPLIANCE_OFFICER);
  };

  const handleEditMLCompliance = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.ML_COMPLIANCE);
  };

  const handleEditAirReport = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.AIR_REPORT);
  };

  const handleEditCompanyType = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.COMPANY_TYPE);
  };

  const handleEditFinancialLocation = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.FINANCIAL_LOCATION);
  };

  const handleEditAppointedAgent = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.APPOINTED_AGENT);
  };

  const handleEditFiscalEndDate = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.FISCAL_END_DATE);
  };

  const handleEditCriminalRecord = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.CRIMINAL_RECORD);
  };

  const handleEditProofInsurance = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.PROOF_INSURANCE_OMISSIONS);
  };

  const handleEditTrustAccount = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.TRUST_ACCOUNT);
  };

  const SigningOfficerBlock = ({ data = [] }) => {
    return (
      <div className="row">
        {data.map((element, index) => (
          <div className="col-lg-12">{element}</div>
        ))}
      </div>
    );
  };

  function getPeopleCRCList() {
    if (!peopleList || peopleList.length === 0) {
      return null;
    }

    const noChangeIds = mraRenewalCrcNoChangeIds.split(",");
    const updatedFileIds = mraRenewalCrcUpdatedIds.split(",");

    const findLatestDocument = (docList, entityId) => {
      if (!docList) {
        return null;
      }

      const filteredAndSortedDocs = docList
        .filter((document) => +document.entityId === +entityId)
        .sort((document1, document2) => {
          const create1 = document1.createdDate ?? Date.now();
          const create2 = document2.createdDate ?? Date.now();
          return create1 < create2 ? 1 : -1;
        });

      if (filteredAndSortedDocs.length > 0) {
        return filteredAndSortedDocs[0];
      }

      return null;
    };

    const crcSectionList = [];

    peopleList.forEach((rjscOfficer, i) => {
      const { entityId } = rjscOfficer;

      let newlyUploadedCRC = false;
      let noChangeInCRC = false;

      if (noChangeIds.findIndex((id) => +id === +entityId) !== -1) {
        noChangeInCRC = true;
      } else if (updatedFileIds.findIndex((id) => +id === +entityId) !== -1) {
        newlyUploadedCRC = true;
      }

      const latestDocument = findLatestDocument(uploadCriminalRecordCheck, entityId);

      crcSectionList.push(
        <div key={i}>
          <ReviewForm
            data={{
              Name: fullName(rjscOfficer),
              Relationship: rjscOfficer?.position,
              File: latestDocument ? documentReviewComponent(latestDocument) : "No file uploaded"
            }}
          />
          {noChangeInCRC && <div>No change to CRC or not required to upload CRC.</div>}
          {newlyUploadedCRC && <div>New file uploaded in renewal.</div>}
          <SubSeparator />
        </div>
      );
    });

    return crcSectionList;
  }

  return (
    <>
      <div className={"container"}>
        <TopNavigationBar {...props} />
        <div className="row">
          <div className="col-xs-12 col-lg-9">
            {applicationType === APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE && (
              <Header
                id="broker-application-review-header"
                header="Mortgage Lender Licence renewal"
              />
            )}
            {applicationType === APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE && (
              <Header
                id="broker-application-review-header"
                header="Mortgage Administrator Licence renewal"
              />
            )}
            <Paragraph
              id="broker-application-review-text"
              text={["Please review and ensure the information you have provided is correct."]}
            />
          </div>
        </div>
        <ComplianceOfficePersonalInfo
          personData={agencyApplication?.businessLicenceData?.personData}
          handleEditPersonalInformation={handleEditCOPersonalInformation}
        />
        <ComplianceOfficerQualifications
          mortgageCacheProperties={mortgageCacheProperties}
          handleEditCOQualifications={handleEditCOQualifications}
        />

        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{ marginLeft: "15px" }}>
            {applicationType === APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE && (
              <Paragraph
                style={{ paddingTop: "10px" }}
                id="broker-application-review-text"
                text={["<h3>Compliance Officer - MRA and regulations confirmation<h3>"]}
              />
            )}
            {applicationType === APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE && (
              <Paragraph
                style={{ paddingTop: "10px" }}
                id="broker-application-review-text"
                text={["<h3>Compliance Officer - MRA and regulations confirmation<h3>"]}
              />
            )}
            <ReviewForm
              data={{
                "Are you in compliance with the MRA?": agencyApplication.businessLicenceData
                  .isMRACompliance
                  ? "Yes"
                  : "No"
              }}
            />
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration: "underline" }}
              buttonType={"regular_link"}
              text={
                applicationType === APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE
                  ? "Update Mortgage Administrator compliance"
                  : "Update Mortgage Lender compliance"
              }
              onClick={handleEditMLCompliance}
            />
          </div>
        </div>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{ marginLeft: "15px" }}>
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Annual Information Return (AIR)<h3>"]}
            />
            <ReviewForm
              data={{
                "Has the AIR been submitted": agencyApplication.businessLicenceData.isAIRSubmitted
                  ? "Yes"
                  : "No"
                // "Uploaded AIR File:":
                //   uploadedAIRDocument.length > 0
                //     ? documentReviewComponent(
                //       uploadedAIRDocument[0]
                //       )
                //     : ""
              }}
            />
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration: "underline" }}
              buttonType={"regular_link"}
              text={"Update AIR Status"}
              onClick={handleEditAirReport}
            />
          </div>
        </div>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{ marginLeft: "15px" }}>
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Company type<h3>"]}
            />
            <ReviewForm
              data={{
                "Company type selected":
                  COMPANY_TYPES[agencyApplication.businessLicenceData.companyType]
              }}
            />
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration: "underline" }}
              buttonType={"regular_link"}
              text={"Update Company type"}
              onClick={handleEditCompanyType}
            />
          </div>
        </div>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{ marginLeft: "15px" }}>
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Financial Records Location<h3>"]}
            />
            <ReviewForm
              data={{
                "Civic address": financialRecordsLocation?.street ?? "",
                "City/Town": financialRecordsLocation?.city ?? "",
                Province: financialRecordsLocation?.province ?? "",
                "Postal code": financialRecordsLocation?.postalCode ?? ""
              }}
            />
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration: "underline" }}
              buttonType={"regular_link"}
              text={"Update financial records location"}
              onClick={handleEditFinancialLocation}
            />
          </div>
        </div>
        {applicationType === APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE && (
          <>
            <MainSeparator />
            <div className="row">
              <div className="col-lg-10" style={{ marginLeft: "15px" }}>
                <Paragraph
                  style={{ paddingTop: "10px" }}
                  id="broker-application-review-text"
                  text={["<h3>Financial Statements<h3>"]}
                />
                <ReviewForm
                  data={{
                    "Have you submitted the Financial Statements?": agencyApplication
                      .businessLicenceData?.isFinancialStatementSubmitted
                      ? "Yes"
                      : "No"
                  }}
                />
              </div>
            </div>
          </>
        )}
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{ marginLeft: "15px" }}>
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Appointed agent<h3>"]}
            />
            <ReviewForm
              data={{
                Name: fullName(responsibleAgent)
              }}
            />
            <LineSeperator />
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Service address<h3>"]}
            />
            <ReviewForm
              data={{
                "Civic address": serviceBusinessAddress?.street ?? "",
                "City/Town": serviceBusinessAddress?.city ?? "",
                Province: serviceBusinessAddress?.province ?? "",
                "Postal code": serviceBusinessAddress?.postalCode ?? ""
              }}
            />
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration: "underline" }}
              buttonType={"regular_link"}
              text={"Update appointed agent"}
              onClick={handleEditAppointedAgent}
            />
          </div>
        </div>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{ marginLeft: "15px" }}>
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Fiscal year end date<h3>"]}
            />
            <ReviewForm data={{ Date: formatDate(companyFiscalEndDate) }} />
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration: "underline" }}
              buttonType={"regular_link"}
              text={"Update fiscal end date"}
              onClick={handleEditFiscalEndDate}
            />
          </div>
        </div>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{ marginLeft: "15px" }}>
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Errors and Omissions Insurance<h3>"]}
            />
            <ReviewForm
              data={{
                //TODO: No should hide the file display
                "Do you have Errors and Omissions insurance?": agencyApplication.businessLicenceData
                  .hasActiveErrorsOmissionsInsurance
                  ? "Yes"
                  : "No",
                "Continuation certificate:":
                  uploadedProofOfInsurance.length > 0
                    ? documentReviewComponent(uploadedProofOfInsurance[0])
                    : "",
                "Continuation certificate expiry:": humanReadableDate(
                  agencyApplication.businessLicenceData.insuranceContinuationExpiryDate
                )
              }}
            />
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration: "underline" }}
              buttonType={"regular_link"}
              text={"Update Errors and Omissions Insurance certificate"}
              onClick={handleEditProofInsurance}
            />
          </div>
        </div>
        {/* Trust Account */}
        {!isLender && (
          <>
            <MainSeparator />
            <div className="row">
              <div className="col-lg-11" style={{ marginLeft: "15px" }}>
                <Paragraph
                  style={{ paddingTop: "10px" }}
                  id="broker-application-review-text"
                  text={["<h3>Trust Account<h3>"]}
                />
                <ReviewForm
                  data={{
                    "Financial Institution": trustAccountInstitution?.branchName ?? "",
                    "Branch address": trustAccountInstitution?.address?.singleLineAddress ?? "",
                    "City/Town": trustAccountInstitution?.address?.city ?? "",
                    Province: trustAccountInstitution?.address?.province ?? "",
                    "Postal code": trustAccountInstitution?.address?.postalCode ?? "",
                    "Account number": trustAccountInstitution?.accountNumber ?? "",
                    "Name on account": trustAccountInstitution?.name ?? "",
                    "Signing Officers":
                      trustAccountInstitution?.signingOfficers?.length > 0 ? (
                        <SigningOfficerBlock data={trustAccountInstitution?.signingOfficers} />
                      ) : (
                        ""
                      )
                  }}
                />
              </div>
              <div className="col-lg-3">
                <Button
                  style={{ marginTop: "10px", textDecoration: "underline" }}
                  buttonType={"regular_link"}
                  text={"Update trust account"}
                  onClick={handleEditTrustAccount}
                />
              </div>
            </div>
          </>
        )}
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{ marginLeft: "15px" }}>
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Criminal Record Checks<h3>"]}
            />
            {getPeopleCRCList()}
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration: "underline" }}
              buttonType={"regular_link"}
              text={"Update criminal record check"}
              onClick={handleEditCriminalRecord}
            />
          </div>
        </div>

        <div className="row topMarginLarge">
          <div className="col-lg-4">
            <Button
              id="complete-mortgage-education-continue-button"
              className="btn-lg btn-default"
              buttonType="regular_primary"
              text="Yes, this information is correct"
              onClick={() => {
                // Set MORTGAGE PROP value
                dispatch(
                  setMortgageProperty({
                    key: MORTGAGE_APPLICATION_PROPERTY,
                    value: true
                  })
                );

                dispatch(saveBusinessLicenceApplicationData(applicationType, applicationMode))
                  .then((data) => {
                    let nextUrl = ROUTE_PATH.ML_APP;
                    if (applicationType === APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE) {
                      nextUrl = ROUTE_PATH.MA_APP;
                    }
                    // On to the next item
                    dispatch(push(`${nextUrl}${ROUTE_PATH.RENEWAL_CONFIRMATION_BEFORE_PAYMENT}`));
                  })
                  .catch((err) => {
                    // TODO : error check
                  });
              }}
            />
          </div>
        </div>
      </div>

      <Modal
        appElement={document.getElementById("root")}
        isOpen={showEditDialog}
        contentLabel="editDetailsDialog"
        onRequestClose={() => {
          setShowEditDialog(false);
        }}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        className="modalDialogContent editDetailsModalDialog"
        closeTimeoutMS={150}
      >
        <div className="row">
          <div className="col-11">
            <div className="row">
              <div className="col-12">
                <h2>{titleForComponent(editComponent)}</h2>
                <MainSeparator />
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.ML_COMPLIANCE && (
                  <RenewalRegulationAct
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.AIR_REPORT && (
                  <ConfirmAIRSubmission
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.CO_PERSONAL_INFO && (
                  <RenewalComplianceOfficerInformation
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.COMPLIANCE_OFFICER && (
                  <RenewalComplianceOfficerQualification
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.COMPANY_TYPE && (
                  <RenewalCompanyType
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.COMPANY_CONTACT && (
                  <CompanyContactInformation
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.FINANCIAL_LOCATION && (
                  <RenewalCompanyFinancialLocation
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.APPOINTED_AGENT && (
                  <RenewalAppointedAgentServiceAddress
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.FISCAL_END_DATE && (
                  <RenewalCompanyFiscalEndDate
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.CRIMINAL_RECORD && (
                  <RenewalCriminalRecordCheck
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.PROOF_INSURANCE_OMISSIONS && (
                  <RenewalProofOfInsurance
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.TRUST_ACCOUNT && (
                  <RenewalTrustAccount
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="col-1">
            <div
              className="closeButton pull-right"
              onClick={() => {
                setShowEditDialog(false);
              }}
            >
              <i className="fa fa-2x fa-times"></i>
            </div>
          </div>
        </div>
      </Modal>

      <DocumentViewer
        fileData={documentViewerDocument}
        fileName={selectedFile?.filename}
        isOpen={openDocumentViewer}
        handleClose={() => setOpenDocumentViewer(false)}
      />
    </>
  );
};

const ComplianceOfficerQualifications = (props) => {
  if (!props.mortgageCacheProperties) {
    return null;
  }

  const accepted = props.mortgageCacheProperties[
    MORTGAGE_PROPERTY_KEYS.IS_CO_QUALIFICATIONS_ACCEPTED
  ]
    ? "    Yes"
    : "    No";

  return (
    <>
      <MainSeparator />
      <div className="row">
        <div className="col-lg-10" style={{ marginLeft: "15px" }}>
          <Paragraph
            style={{ paddingTop: "10px" }}
            id="compliance-officer-review-text"
            text={["<h3>Compliance Officer qualifications confirmation<h3>"]}
          />
          <div className={"col-8"}>
            <ol className="COQualificationReqList">
              <li>
                Is the Compliance Officer <b>19 years of age or older? &nbsp; &nbsp;{accepted}</b>
              </li>
              <li>
                Is the Compliance Officer a <b>resident of Canada? &nbsp; &nbsp;{accepted}</b>
              </li>
              <li>
                The Compliance Officer is engaged in the activities of the licensee to the extent
                that the individual is capabale of carrying out their duties and responsibilities
                under the Act and its regulations.
                <b> &nbsp; &nbsp;{accepted}</b>
              </li>
            </ol>
          </div>
        </div>
        <div className="col-lg-3">
          <Button
            style={{ marginTop: "10px", textDecoration: "underline" }}
            buttonType={"regular_link"}
            text={"Update qualifications status"}
            onClick={props.handleEditCOQualifications}
          />
        </div>
      </div>
    </>
  );
};

const ComplianceOfficePersonalInfo = (props) => {
  const personData = props.personData;
  if (!personData) {
    return null;
  }

  return (
    <>
      <MainSeparator />
      <div className="row">
        <div className="col-lg-8">
          <h3>Compliance Officer personal information</h3>
          <ReviewForm
            fullSize
            data={{
              "First name": personData?.firstName ?? "",
              "Last name": personData?.lastName ?? "",
              "Home Address": personData?.addressData?.address1 ?? "",
              "City/Town": personData?.addressData?.cityName ?? "",
              Province: personData?.addressData?.stateName ?? "",
              "Postal code": personData?.addressData?.postalCode ?? "",
              "Phone number": personData?.phoneNumber ?? "",
              "Alternate phone number": personData?.alternatePhoneNumber ?? "",
              Email: personData?.email ?? ""
            }}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-lg-3">
          <Button
            style={{ marginTop: "10px", textDecoration: "underline" }}
            buttonType={"regular_link"}
            text={"Update Compliance Officer's personal information"}
            onClick={props.handleEditPersonalInformation}
          />
        </div>
      </div>
    </>
  );
};
